import { LangLink } from "@/components/LangLink";
import { useLanguage } from "@/lib/language";
import type { InsightMeta } from "@/components/insights/InsightShell";
import { H2, P, TableWrap, thClass, tdClass } from "@/components/insights/prose";

export const certificateScopeMeta: InsightMeta = {
  slug: "iso-certification-scope-activities-sites-iraq",
  metaTitle: {
    en: "ISO Certificate Scope: Activities & Sites in Iraq | IraqCert",
    ar: "نطاق شهادة الأيزو: الأنشطة والمواقع في العراق | عراق سيرت",
  },
  title: {
    en: "ISO Certificate Scope: Activities and Sites for Iraqi Companies",
    ar: "نطاق شهادة الأيزو: الأنشطة والمواقع المشمولة للشركات العراقية",
  },
  description: {
    en: "Understand what an ISO management-system certificate covers: the named organization, activities and sites, and questions to resolve before certification.",
    ar: "افهم ما تشمله شهادة نظام الإدارة: المؤسسة المذكورة والأنشطة والمواقع، والأسئلة التي ينبغي حسمها قبل الحصول على الشهادة.",
  },
  datePublished: "2026-09-23",
  dateModified: "2026-09-29",
  dateLabel: {
    en: "29 September 2026",
    ar: "29 أيلول 2026",
  },
  enquiryTitle: {
    en: "Discuss your activities and sites",
    ar: "ناقش الأنشطة والمواقع المطلوب شمولها",
  },
  enquiryButton: {
    en: "Discuss your activities and sites",
    ar: "ناقش الأنشطة والمواقع المطلوب شمولها",
  },
  enquiryMessage: {
    en: "Hello IraqCert, I read your guide to ISO certificate scope. I would like to clarify preparation support for my activities and sites.\nStandard or buyer requirement:\nActivities to be covered:\nOperating locations:\nPage: /insights/iso-certification-scope-activities-sites-iraq",
    ar: "مرحباً عراق سيرت، قرأت دليل نطاق شهادة الأيزو، وأرغب في تحديد دعم التحضير اللازم لأنشطتي ومواقعي.\nالمواصفة أو متطلب الجهة المشترية:\nالأنشطة المطلوب شمولها:\nمواقع العمل:\nالصفحة: /ar/insights/iso-certification-scope-activities-sites-iraq",
  },
  faq: [
    {
      q: {
        en: "Does one ISO certificate cover all my branches?",
        ar: "هل تشمل شهادة الأيزو الواحدة جميع فروع شركتي؟",
      },
      a: {
        en: "Not automatically. Check the certification documents and any site list. Ask the issuing body to clarify which locations and activities are covered before using the certificate for another branch.",
        ar: "ليس تلقائياً. راجع وثائق الشهادة وأي قائمة للمواقع، واطلب من الجهة المانحة توضيح المواقع والأنشطة المشمولة قبل استخدام الشهادة لفرع آخر.",
      },
    },
    {
      q: {
        en: "Does ISO 9001 certification certify every product I sell?",
        ar: "هل تعني شهادة ISO 9001 أن كل منتج أبيعه حاصل على شهادة مطابقة؟",
      },
      a: {
        en: "No. ISO 9001 concerns a quality management system, not individual product certification. A buyer may have separate product specifications or conformity-assessment requirements that the management-system certificate does not replace.",
        ar: "لا. تتعلق ISO 9001 بنظام إدارة الجودة، لا بمنح شهادة لكل منتج على حدة. وقد يشترط المشتري مواصفات للمنتج أو متطلبات لتقييم المطابقة لا تحلّ شهادة نظام الإدارة محلها.",
      },
    },
    {
      q: {
        en: "Can I add an activity by changing the certificate wording?",
        ar: "هل يمكن إضافة نشاط بمجرد تغيير نص الشهادة؟",
      },
      a: {
        en: "A wording change alone does not establish coverage. Describe the proposed addition to the certification body and obtain its assessment of the scope and any required certification work.",
        ar: "لا يثبت تعديل النص وحده شمول النشاط. صف الإضافة المقترحة لجهة منح الشهادات واطلب تقييمها للنطاق وأي أعمال لازمة لمنح الشهادة.",
      },
    },
  ],
};

const comparisonRows = [
  [
    {
      en: "Named organization",
      ar: "المؤسسة المذكورة",
    },
    {
      en: "Which organization the document identifies.",
      ar: "هوية المؤسسة التي تخصها الوثيقة.",
    },
    {
      en: "Is this the company I am contracting with?",
      ar: "هل هي الشركة التي سأتعاقد معها؟",
    },
  ],
  [
    {
      en: "Activities",
      ar: "الأنشطة",
    },
    {
      en: "The operations described as certified.",
      ar: "العمليات المذكورة ضمن نطاق الشهادة.",
    },
    {
      en: "Does the wording address the work being offered?",
      ar: "هل يصف النص الأعمال المعروضة عليّ؟",
    },
  ],
  [
    {
      en: "Sites",
      ar: "المواقع",
    },
    {
      en: "The locations and any site-specific coverage.",
      ar: "المواقع المشمولة وأي نطاق خاص بموقع معين.",
    },
    {
      en: "Is the relevant operating location included?",
      ar: "هل يشمل النطاق موقع العمل المعني؟",
    },
  ],
  [
    {
      en: "Standard",
      ar: "المواصفة",
    },
    {
      en: "The management-system requirements assessed.",
      ar: "متطلبات نظام الإدارة التي جرى تقييمها.",
    },
    {
      en: "Is this the standard named in my requirement?",
      ar: "هل هذه هي المواصفة المطلوبة؟",
    },
  ],
];

export function CertificateScopeBody() {
  const { t } = useLanguage();
  return (
    <>
      <P>
        {t({
          en: "An ISO standard number alone does not tell a buyer what your certificate covers. Review the named organization, the activities described and the sites included in the certification documents. Before agreeing a quotation, confirm the proposed coverage with the certification body and compare it with the requirement you need to meet.",
          ar: "لا يوضح رقم مواصفة الأيزو وحده للمشتري ما تشمله شهادتك. راجع اسم المؤسسة والأنشطة الموصوفة والمواقع المشمولة في وثائق الشهادة. وقبل الاتفاق على عرض السعر، أكّد التغطية المقترحة مع جهة منح الشهادات وقارنها بالمتطلب الذي تحتاج إلى استيفائه.",
        })}
      </P>
      <P>
        {t({
          en: "If a customer has requested ISO certification, share the request and a description of your activities and locations with IraqCert. We use those details to define the proposed scope and preparation support. The independent certification body assesses the scope and makes the certification decision.",
          ar: "إذا طلب عميل من شركتك شهادة أيزو، شارك الطلب ووصف أنشطتك ومواقع عملك مع IraqCert. نستخدم هذه التفاصيل لتحديد نطاق الشهادة المقترح ودعم التحضير اللازم. وتتولى جهة منح الشهادات المستقلة تقييم النطاق واتخاذ قرار منح الشهادة.",
        })}
      </P>
      <P>
        {t({
          en: "The ISO/IAF Auditing Practices Group’s guidance distinguishes the scope of a quality management system, the certification scope and the scope of an individual audit. These can differ. It also explains that certification may cover only part of an organization. This is educational auditing guidance, not a substitute for the standard or your buyer’s conditions.",
          ar: "تميّز إرشادات مجموعة ممارسات التدقيق التابعة لـISO وIAF بين نطاق نظام إدارة الجودة ونطاق الشهادة ونطاق تدقيق محدد، وقد تختلف هذه النطاقات. وتوضح أيضاً أن الشهادة قد تشمل جزءاً من المؤسسة فقط. وهذه إرشادات تعليمية للتدقيق، وليست بديلاً عن المواصفة أو شروط الجهة المشترية.",
        })}{" "}
        <a
          href="https://committee.iso.org/files/live/sites/tc176/files/PDF%20APG%20New%20Disclaimer%2012-2023/ISO-TC%20176-TF_APG-Scope%20and%20app%20rev%2012-01-2023%20ed1.pdf"
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-primary underline underline-offset-2"
        >
          {t({
            en: "ISO/IAF guidance on scope and applicability",
            ar: "إرشادات ISO وIAF بشأن النطاق وقابلية التطبيق",
          })}
        </a>
      </P>
      <H2>{t({ en: "Read the scope as a buyer would", ar: "اقرأ النطاق من منظور المشتري" })}</H2>
      <P>
        {t({
          en: "Use this table to frame questions about the proposed certificate. Compare the answers with the actual work your company intends to supply.",
          ar: "استخدم هذا الجدول لصياغة أسئلتك عن الشهادة المقترحة، وقارن الإجابات بالأعمال التي تنوي شركتك تقديمها فعلياً.",
        })}
      </P>
      <TableWrap>
        <thead className="bg-accent">
          <tr>
            <th scope="col" className={thClass}>
              {t({ en: "Check", ar: "ما الذي تراجعه؟" })}
            </th>
            <th scope="col" className={thClass}>
              {t({ en: "What it tells you", ar: "ما الذي يوضحه؟" })}
            </th>
            <th scope="col" className={thClass}>
              {t({ en: "Buyer question", ar: "سؤال من منظور المشتري" })}
            </th>
          </tr>
        </thead>
        <tbody>
          {comparisonRows.map((row) => (
            <tr key={row[0].en} className="border-t border-border">
              {row.map((cell, index) =>
                index === 0 ? (
                  <th
                    scope="row"
                    key={cell.en}
                    className={`${tdClass} font-semibold text-foreground`}
                  >
                    {t(cell)}
                  </th>
                ) : (
                  <td key={cell.en} className={tdClass}>
                    {t(cell)}
                  </td>
                ),
              )}
            </tr>
          ))}
        </tbody>
      </TableWrap>
      <H2>
        {t({ en: "Two hypothetical scope mismatches", ar: "مثالان افتراضيان لاختلاف النطاق" })}
      </H2>
      <P>
        {t({
          en: "An equipment trader wants to supply items purchased from manufacturers. Calling its own activity manufacturing would give the buyer a different picture of the business. Explain who performs each activity and what your company controls; ask the certification body to assess suitable scope wording rather than copying another supplier’s certificate.",
          ar: "يرغب تاجر معدات في توريد أصناف يشتريها من مصانع. إن وصف نشاطه بأنه تصنيع يعطي المشتري صورة مختلفة عن طبيعة عمله. وضّح من ينفّذ كل نشاط وما الذي تتحكم فيه شركتك، واطلب من جهة المنح تقييم الصياغة المناسبة للنطاق بدلاً من نسخ شهادة مورد آخر.",
        })}
      </P>
      <P>
        {t({
          en: "A service company has an administrative office and a separate maintenance workshop. Its customer needs assurance relevant to maintenance work. Do not assume that a document describing office activities answers that request. Put the workshop and its role into the discussion before the audit arrangements and quotation are agreed.",
          ar: "لدى شركة خدمات مكتب إداري وورشة صيانة منفصلة، ويطلب عميلها ما يثبت شمول أعمال الصيانة. لا تفترض أن وثيقة تصف أنشطة المكتب تلبي هذا الطلب. ناقش الورشة ودورها قبل الاتفاق على ترتيبات التدقيق وعرض السعر.",
        })}
      </P>
      <H2>
        {t({
          en: "Can one certificate cover several sites?",
          ar: "هل يمكن لشهادة واحدة أن تشمل عدة مواقع؟",
        })}
      </H2>
      <P>
        {t({
          en: "Multi-site certification is possible under applicable arrangements, but it is not automatic. IAF MD 1:2023 sets conditions for multi-site management-system certification and requires certification documents to identify the covered sites and activities. The certification body determines the applicable approach; not every organization qualifies for sampling.",
          ar: "يمكن منح شهادة تشمل عدة مواقع وفق الترتيبات المنطبقة، لكن ذلك ليس تلقائياً. يحدد IAF MD 1:2023 شروط منح شهادات أنظمة الإدارة للمؤسسات متعددة المواقع، ويشترط تحديد المواقع والأنشطة المشمولة في وثائق الشهادة. وتحدد الجهة المانحة المنهج المنطبق؛ فلا تتأهل كل مؤسسة لتدقيق عيّنة من المواقع.",
        })}{" "}
        <a
          href="https://iaf.nu/iaf_system/uploads/documents/IAF_MD1_Issue_3_181020231.pdf"
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-primary underline underline-offset-2"
        >
          {t({
            en: "IAF MD 1 on multi-site certification",
            ar: "وثيقة IAF MD 1 بشأن الشهادات متعددة المواقع",
          })}
        </a>
      </P>
      <P>
        {t({
          en: "For your enquiry, describe what happens at each location and how the operations are managed together. If a certificate refers to a site list or annex, review that document too. A shared brand name, common owner or head-office address is not enough information to confirm coverage.",
          ar: "عند الاستفسار، صف ما يجري في كل موقع وكيف تُدار العمليات معاً. وإذا أحالت الشهادة إلى قائمة مواقع أو ملحق، فراجعه أيضاً. لا يكفي اشتراك المواقع في العلامة التجارية أو المالك أو عنوان المقر الرئيسي لتأكيد شمولها.",
        })}
      </P>
      <H2>
        {t({
          en: "Keep the scope aligned with changes",
          ar: "راجع النطاق عند تغيّر الأنشطة أو المواقع",
        })}
      </H2>
      <P>
        {t({
          en: "Tell the certification body when you plan a new activity, workshop or branch and ask what review is needed. Do not present the existing certificate as covering the addition before its status is confirmed. Keep the buyer’s request, proposed wording and the body’s response together so different teams do not rely on different assumptions.",
          ar: "أبلغ جهة منح الشهادات عندما تخطط لإضافة نشاط أو ورشة أو فرع، واسأل عن المراجعة اللازمة. لا تقدّم الشهادة الحالية على أنها تشمل الإضافة قبل تأكيد وضعها. احتفظ بطلب المشتري والصياغة المقترحة ورد الجهة المانحة معاً حتى لا تعمل فرق الشركة وفق افتراضات مختلفة.",
        })}{" "}
        <LangLink
          to="/insights/compare-iso-certification-quotes-iraq"
          className="font-semibold text-primary underline underline-offset-2"
        >
          {t({
            en: "Compare quotations using the same proposed scope",
            ar: "قارن عروض الأسعار على أساس النطاق المقترح نفسه",
          })}
        </LangLink>
      </P>
      <P>
        {t({
          en: "If your buyer asks for a quality management system, read about ISO 9001 preparation support. Share your activities, operating locations and buyer wording with IraqCert to discuss the support needed. IraqCert helps with preparation and coordination; the independent certification body assesses the scope and decides whether to grant certification.",
          ar: "إذا طلب المشتري نظاماً لإدارة الجودة، فاطّلع على دعم التحضير لشهادة ISO 9001. شارك أنشطتك ومواقع عملك ونص متطلب المشتري مع عراق سيرت لمناقشة الدعم اللازم. تساعد عراق سيرت في التحضير والتنسيق؛ وتتولى جهة المنح المستقلة تقييم النطاق واتخاذ قرار منح الشهادة.",
        })}{" "}
        <LangLink
          to="/services/iso-9001"
          className="font-semibold text-primary underline underline-offset-2"
        >
          {t({ en: "ISO 9001 certification support", ar: "دعم الحصول على شهادة ISO 9001" })}
        </LangLink>
      </P>
    </>
  );
}
